import { useCallback, useEffect, useRef, useState } from "react";
import { getApiService } from "../services/api";
import type { ApiStock } from "../services/api";
import { useLivePrice } from "../hooks/useLivePrice";
import { InfiniteScroll } from "../components/InfiniteScroll";
import { KlineChart } from "../components/KlineChart";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { HoldingDialog } from "../components/HoldingDialog";
import { AddPositionDialog } from "../components/AddPositionDialog";
import { ReducePositionDialog } from "../components/ReducePositionDialog";
import { fmtNum, fmtPercent, percentClass, exchangeUrl } from "../services/format";

const PAGE_SIZE = 10;

// 持仓列表项：名称/代码、每秒刷新的最新价与涨跌幅、K线图、持仓信息、操作按钮
function HoldingItem({
  stock,
  onAdd,
  onReduce,
  onDelete,
}: {
  stock: ApiStock;
  onAdd: (s: ApiStock) => void;
  onReduce: (s: ApiStock) => void;
  onDelete: (s: ApiStock) => void;
}) {
  const live = useLivePrice(`${stock.exchange}${stock.code}`);
  const url = exchangeUrl(stock.exchange, stock.code);
  const profit = (() => {
    if (live.price == null || !stock.costPrice || !stock.quantity) return null;
    return (live.price - stock.costPrice) * stock.quantity;
  })();

  return (
    <div className="item-card mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{stock.name}</span>
          <span className="flat text-12px">
            {stock.exchange}
            {stock.code}
          </span>
        </div>
        <div className="flex items-center gap-2 text-13px">
          <span className={`font-semibold ${percentClass(live.percent)}`}>
            {fmtNum(live.price)}
          </span>
          <span className={percentClass(live.percent)}>
            {fmtPercent(live.percent == null ? null : live.percent)}
          </span>
        </div>
      </div>

      <KlineChart klines={stock.klines ?? []} />

      <div className="mt-2 text-13px flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 flat">
          <span>
            持股 <b>{fmtNum(stock.quantity, 0)}</b> 股
          </span>
          <span>
            成本 <b>{fmtNum(stock.costPrice)}</b>
          </span>
          {profit != null && (
            <span className={profit >= 0 ? "up" : "down"}>
              盈亏 {profit >= 0 ? "+" : ""}
              {fmtNum(profit)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2 sm:mt-0">
          {url && (
            <a
              className="flat text-12px"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              交易所 ↗
            </a>
          )}
          <button className="btn btn-sm" onClick={() => onAdd(stock)}>
            加仓
          </button>
          <button className="btn btn-sm" onClick={() => onReduce(stock)}>
            减仓
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => onDelete(stock)}>
            删除
          </button>
        </div>
      </div>
    </div>
  );
}

// 持仓 Tab
export function HoldingsTab() {
  const api = getApiService();
  const [stocks, setStocks] = useState<ApiStock[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // 搜索
  const [keyword, setKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<ApiStock[]>([]);
  const [noStocks, setNoStocks] = useState<{ code: string; name: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // 对话框
  const [holdingStock, setHoldingStock] = useState<ApiStock | null>(null);
  const [addStock, setAddStock] = useState<ApiStock | null>(null);
  const [reduceStock, setReduceStock] = useState<ApiStock | null>(null);
  const [deleteStock, setDeleteStock] = useState<ApiStock | null>(null);
  const [toast, setToast] = useState("");

  const loadPage = useCallback(
    async (p: number, replace = false) => {
      setLoading(true);
      try {
        const res = await api.getAccountStocks(p, PAGE_SIZE);
        const list = res.data?.stocks ?? [];
        // 空页兜底：追加页没有数据时立即停止后续加载
        if (!replace && list.length === 0) {
          setTotal(0);
          return;
        }
        setTotal(res.data?.pagination?.total ?? 0);
        setStocks((prev) => (replace ? list : [...prev, ...list]));
        setPage(p); // 记录已加载页码，避免重复请求同一页
      } catch {
        // 401 等错误由 AuthContext 统一处理
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage]);

  // 搜索防抖
  useEffect(() => {
    if (!keyword.trim()) {
      setSearchResults([]);
      setNoStocks([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.searchStocks(keyword.trim());
        setSearchResults(res.data?.stocks ?? []);
        setNoStocks(res.data?.noStocks ?? []);
        setShowResults(true);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword, api]);

  // 点击搜索框外部关闭下拉
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const flashToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const confirmHolding = async (quantity: number, price: number) => {
    if (!holdingStock) return;
    const res = await api.addToFavorites(holdingStock.id!, price, quantity);
    if (res.code === 200) {
      flashToast("已添加到持仓");
      setHoldingStock(null);
      setShowResults(false);
      setKeyword("");
      loadPage(1, true);
    } else {
      flashToast(res.message ?? "添加失败");
    }
  };

  const confirmAdd = async (price: number, amount: number) => {
    if (!addStock) return;
    const qty = Math.floor(amount / price);
    const res = await api.updateAccountStock(addStock.id!, qty, price, "add");
    if (res.code === 200) {
      flashToast("加仓成功");
      setAddStock(null);
      loadPage(1, true);
    } else {
      flashToast(res.message ?? "加仓失败");
    }
  };

  const confirmReduce = async (price: number, quantity: number) => {
    if (!reduceStock) return;
    const res = await api.updateAccountStock(reduceStock.id!, quantity, price, "reduce");
    if (res.code === 200) {
      flashToast("减仓成功");
      setReduceStock(null);
      loadPage(1, true);
    } else {
      flashToast(res.message ?? "减仓失败");
    }
  };

  const confirmDelete = async () => {
    if (!deleteStock) return;
    const res = await api.removeFromFavorites(deleteStock.id!);
    if (res.code === 200) {
      flashToast("已删除持仓");
      setDeleteStock(null);
      loadPage(1, true);
    } else {
      flashToast(res.message ?? "删除失败");
    }
  };

  return (
    <div className="pt-2">
      {/* 搜索 */}
      <div className="relative mb-4" ref={searchBoxRef}>
        <input
          className="input"
          placeholder="搜索股票（代码 / 名称）"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onFocus={() => keyword.trim() && setShowResults(true)}
        />
        {showResults && (keyword.trim() || searching) && (
          <div className="card absolute left-0 right-0 top-full mt-1 overflow-hidden" style={{ zIndex: 30 }}>
            {searching ? (
              <div className="p-3 flat text-13px">搜索中...</div>
            ) : searchResults.length > 0 ? (
              searchResults.map((s) => (
                <button
                  key={s.id}
                  className="btn w-full justify-start rounded-none text-left"
                  style={{ border: "none", borderBottom: "1px solid var(--border)" }}
                  onClick={() => setHoldingStock(s)}
                >
                  <span className="font-medium">{s.name}</span>
                  <span className="flat text-12px ml-2">
                    {s.exchange}
                    {s.code}
                  </span>
                </button>
              ))
            ) : noStocks.length > 0 ? (
              <div className="p-3 flat text-13px">
                找到未入库股票：{noStocks.map((s) => `${s.name}(${s.code})`).join("、")}
                ，请先通过任务同步K线
              </div>
            ) : (
              <div className="p-3 flat text-13px">未找到相关股票</div>
            )}
          </div>
        )}
      </div>

      {/* 持仓列表 */}
      {initialLoading ? (
        <div className="flat text-center py-10">加载中...</div>
      ) : stocks.length === 0 && !loading ? (
        <div className="flat text-center py-10">暂无持仓，搜索股票添加吧</div>
      ) : (
        <InfiniteScroll
          hasMore={stocks.length < total}
          loading={loading}
          onLoadMore={() => loadPage(page + 1)}
        >
          {stocks.map((s) => (
            <HoldingItem
              key={`${s.id}-${s.quantity}-${s.costPrice}`}
              stock={s}
              onAdd={setAddStock}
              onReduce={setReduceStock}
              onDelete={setDeleteStock}
            />
          ))}
        </InfiniteScroll>
      )}

      {/* 对话框 */}
      <HoldingDialog
        open={!!holdingStock}
        stock={holdingStock}
        onClose={() => setHoldingStock(null)}
        onConfirm={confirmHolding}
      />
      <AddPositionDialog
        open={!!addStock}
        stock={addStock}
        onClose={() => setAddStock(null)}
        onConfirm={confirmAdd}
      />
      <ReducePositionDialog
        open={!!reduceStock}
        stock={reduceStock}
        onClose={() => setReduceStock(null)}
        onConfirm={confirmReduce}
      />
      <ConfirmDialog
        open={!!deleteStock}
        title="确认删除"
        message={`确定删除 ${deleteStock?.name ?? ""}（${deleteStock?.exchange}${deleteStock?.code ?? ""}）的持仓吗？`}
        confirmText="删除"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteStock(null)}
      />

      {/* Toast */}
      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-8 px-4 py-2 rounded-full text-13px"
          style={{ background: "var(--chart-tooltip)", color: "var(--chart-tooltip-fg)", zIndex: 200 }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
